import type { CoviActionItem, CoviChatItem } from "../model/types"
import { parseChartSpec } from "./chartSpec"
import { extractMarkdownTables } from "./markdownTables"

type CoviToolItem = Extract<CoviChatItem, { kind: "tool" }>

export type NotebookTableSource = "chart" | "narrative"

export type NotebookCell =
  | { kind: "prompt"; id: string; text: string }
  | { kind: "research"; id: string; items: CoviToolItem[] }
  | {
      kind: "table"
      id: string
      title: string | null
      columns: string[]
      rows: Record<string, unknown>[]
      source: NotebookTableSource
    }
  | { kind: "visualization"; id: string; item: CoviActionItem }
  | {
      kind: "narrative"
      id: string
      item: Extract<CoviChatItem, { kind: "assistant" }>
    }
  | { kind: "action"; id: string; item: CoviActionItem }
  | {
      /** An action outcome restored from a saved notebook; nothing to confirm. */
      kind: "saved-action"
      id: string
      actionKind: string
      status: string
      detail: string | null
      href: string | null
    }
  | { kind: "error"; id: string; text: string }

function chartTableCell(item: CoviActionItem): NotebookCell | null {
  const spec = parseChartSpec(item.proposal.args.spec)
  if (!spec || spec.data.length === 0) {
    return null
  }
  const declared = [
    ...(spec.x ? [spec.x.field] : []),
    ...spec.series.map((series) => series.field),
  ]
  const firstRow = spec.data[0] ?? {}
  const columns = (
    declared.length > 0 ? declared : Object.keys(firstRow)
  ).filter((column, index, all) => all.indexOf(column) === index)
  return {
    kind: "table",
    id: `${item.id}:table`,
    title: spec.title,
    columns,
    rows: spec.data,
    source: "chart",
  }
}

/** Reading order inside one turn: what Sofia did, what it found, then what it said. */
const TURN_CELL_ORDER: Record<NotebookCell["kind"], number> = {
  prompt: 0,
  research: 1,
  table: 2,
  visualization: 3,
  narrative: 4,
  action: 5,
  "saved-action": 5,
  error: 6,
}

/**
 * Sofia's transcript interleaves prose and action cards in arrival order. A
 * notebook reads research → table → chart → narrative → action, so each turn
 * (a prompt and everything up to the next prompt) is stably reordered by kind.
 */
function orderTurns(cells: NotebookCell[]): NotebookCell[] {
  const ordered: NotebookCell[] = []
  let turn: NotebookCell[] = []
  const flushTurn = () => {
    const indexed = turn.map((cell, index) => ({ cell, index }))
    indexed.sort(
      (a, b) =>
        TURN_CELL_ORDER[a.cell.kind] - TURN_CELL_ORDER[b.cell.kind] ||
        a.index - b.index,
    )
    ordered.push(...indexed.map((entry) => entry.cell))
    turn = []
  }
  for (const cell of cells) {
    if (cell.kind === "prompt") {
      flushTurn()
      ordered.push(cell)
      continue
    }
    turn.push(cell)
  }
  flushTurn()
  return ordered
}

/**
 * Presentation adapter from Sofia's transcript to notebook cells. It is a pure
 * projection: the transcript stays the source of truth, so every follow-up
 * derives new cells without mutating earlier ones.
 */
export function projectNotebookCells(items: CoviChatItem[]): NotebookCell[] {
  return orderTurns(projectInArrivalOrder(items))
}

function projectInArrivalOrder(items: CoviChatItem[]): NotebookCell[] {
  const cells: NotebookCell[] = []
  let researchRun: CoviToolItem[] = []
  const flushResearch = () => {
    if (researchRun.length > 0) {
      cells.push({
        kind: "research",
        id: `research:${researchRun[0]?.id ?? cells.length}`,
        items: researchRun,
      })
      researchRun = []
    }
  }
  for (const item of items) {
    if (item.kind === "tool") {
      researchRun.push(item)
      continue
    }
    flushResearch()
    switch (item.kind) {
      case "user":
        // A confirmation click is recorded as a user message so the transcript
        // stays honest; the notebook shows the outcome on the action cell instead.
        if (item.actionConfirmation !== true) {
          cells.push({ kind: "prompt", id: item.id, text: item.text })
        }
        break
      case "assistant": {
        const { tables, remainder } = extractMarkdownTables(item.text)
        tables.forEach((table, index) => {
          cells.push({
            kind: "table",
            id: `${item.id}:table:${index}`,
            title: null,
            columns: table.columns,
            rows: table.rows,
            source: "narrative",
          })
        })
        if (tables.length === 0 || remainder.length > 0) {
          cells.push({
            kind: "narrative",
            id: item.id,
            item: tables.length > 0 ? { ...item, text: remainder } : item,
          })
        }
        break
      }
      case "action": {
        if (item.proposal.kind === "render_visualization") {
          const table = chartTableCell(item)
          if (table) {
            cells.push(table)
          }
          cells.push({ kind: "visualization", id: item.id, item })
        } else {
          cells.push({ kind: "action", id: item.id, item })
        }
        break
      }
      case "error":
        cells.push({ kind: "error", id: item.id, text: item.text })
        break
      default:
        break
    }
  }
  flushResearch()
  return cells
}
